import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { ClipLabelVocabulary } from 'services/VideoEditingService';
import { humanize } from '../format';
import {
  BRIEF_CHOICES, BRIEF_DEFAULTS, BriefDefaultKey, EDITING_DIRECTION_STANDING_PREFIXES,
  EDITING_DIRECTION_TEMPLATE, FINAL_CTA_STANDING_PREFIXES, FINAL_CTA_TEMPLATE,
  HOOK_OVERLAY_STANDING_PREFIXES, HOOK_OVERLAY_TEMPLATE, isOptionalSection, isSectionWritten,
  matchesSharedDefaults, recombineBriefLines, resolveBriefDefaults, splitBriefLines,
  standingSummary,
} from '../briefTemplates';

export type BriefField = 'hookOverlay' | 'editingDirection' | 'finalCta' | 'subtitlesStyle' | 'musicStyle';

// The project's product type and chosen ad copy — the opening on-screen HOOK, the
// Meta HEADLINE, and the closing on-screen CTA — shown beside the video (in the
// stage's side whitespace, not the central video). Each carries an at-a-glance
// set/unset dot and its value, and is editable inline (autosaves to the project).
//
// The product type sits above the copy and comes from the SAME vocabulary the
// b-roll library labels clips with, so a project and the clips it pulls from name
// the product the same way.
interface Props {
  productCategory: string;
  headline: string;
  // The clip-label vocabulary, once the page has loaded it. Null while it loads —
  // the dropdown stays disabled rather than offering a hardcoded copy of the list.
  vocabulary: ClipLabelVocabulary | null;
  onProductCategoryChange: (value: string) => void;
  onHeadlineChange: (value: string) => void;
  // The four brief sections that open the generated instructions.
  hookOverlay: string;
  editingDirection: string;
  finalCta: string;
  subtitlesStyle: string;
  musicStyle: string;
  onBriefFieldChange: (field: BriefField, value: string) => void;
  // Which required fields were empty when Generate was last pressed, if any. Null until
  // someone actually tries to generate — a form that turns red before you have had a
  // chance to fill it in reads as broken rather than as guidance.
  missingRequired?: { hook: boolean; cta: boolean } | null;
  // Opens the shared-defaults editor (the gear). The defaults themselves live in the
  // settings store, not on the project.
  onEditDefaults: () => void;
  // The shared wording, resolved from the settings store, so the panel can show the
  // sentences the [DEFAULT …] tokens stand for. Null while that request is in flight —
  // the pencil stays disabled rather than baking the house wording over the team's.
  briefDefaults: Record<BriefDefaultKey, string> | null;
}

// A muted red rather than the theme's error red: this is a "you still owe this" prompt on
// a form the user is in the middle of, not a failure.
const MISSING_RED = '#e0a3a3';
const MISSING_TEXT = '#b45454';

// The standing wording of one section — the [DEFAULT …] lines, which are almost always
// right and almost never read.
//
// It collapses to ONE row naming what it covers, because the sentences behind those
// tokens run to a paragraph each: showing them in full would trade a form full of
// brackets for a form full of prose and leave the panel taller than it started. The row
// expands to the real wording on click, and the pencil turns it into this project's own
// editable text.
//
// Resolution is for DISPLAY until then. A section still holding its tokens keeps tracking
// the shared wording, so editing the gear reaches it; a section the pencil has been used
// on holds its own copy and stops. That is the whole difference between the two, and it
// is why clicking through to read the wording must not itself store anything.
type StandingMode = 'collapsed' | 'expanded' | 'editing';

// Where the summary text starts, measured rather than guessed: the row's own left padding
// (px: 0.5 → 4px) + the caret (16px) + the gap after it (0.25 → 2px). The expanded wording
// is indented to the same line, so it reads as hanging UNDER the row that opened it instead
// of starting back at the panel edge as a separate block of text.
const STANDING_TEXT_INSET = 4 + 16 + 2;

function StandingBlock({ label, block, templateBlock, briefDefaults, onChange, onReset }: {
  label: string;
  // The section's standing lines exactly as stored — tokens and all.
  block: string;
  // The same lines as the template ships them, to tell "still the shared wording" from
  // "this project's own".
  templateBlock: string;
  briefDefaults: Record<BriefDefaultKey, string> | null;
  onChange: (nextBlock: string) => void;
  onReset: () => void;
}) {
  const [mode, setMode] = React.useState<StandingMode>('collapsed');
  const pencilRef = React.useRef<HTMLButtonElement>(null);
  const returnFocus = React.useRef(false);
  // Derived from the label rather than generated: there is one standing block per section,
  // so this is unique without depending on a hook whose availability varies by React
  // version.
  const regionId = `standing-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  const settled = briefDefaults || BRIEF_DEFAULTS;
  const resolved = resolveBriefDefaults(block, settled);
  const summary = standingSummary(block);
  // By what it SAYS, not by whether a token survived: reading the wording, or opening it
  // and changing nothing, leaves the sentences behind instead of the tokens, and a row
  // that stops saying "defaults" because you looked at it is claiming a divergence that is
  // not there.
  const usingDefaults = matchesSharedDefaults(block, templateBlock, settled);

  // Leaving the editor unmounts it, so focus has to be handed back deliberately or it
  // falls to the document and the next Tab restarts from the top of the page.
  React.useEffect(() => {
    if (mode !== 'editing' && returnFocus.current) {
      returnFocus.current = false;
      pencilRef.current?.focus();
    }
  }, [mode]);

  function stopEditing() {
    returnFocus.current = true;
    setMode('expanded');
  }

  function startEditing() {
    // Never materialise while the shared wording is still loading — that writes the house
    // phrasing over whatever the team saved, with no way back but the reset button.
    if (!briefDefaults) return;
    const materialised = resolveBriefDefaults(block, briefDefaults);
    // The CTA screen's standing lines carry no token at all, so revealing them must not
    // autosave a section nobody has typed in.
    if (materialised !== block) onChange(materialised);
    setMode('editing');
  }

  if (mode === 'editing') {
    return (
      <Box sx={{ mt: 0.5 }}>
        <TextField
          value={block}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') stopEditing(); }}
          autoFocus
          multiline
          minRows={2}
          maxRows={10}
          fullWidth
          size="small"
          inputProps={{ 'aria-label': `${label} standing wording` }}
          InputProps={{ sx: { fontSize: '0.75rem', lineHeight: 1.4 } }}
        />
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
          <Button size="small" onClick={stopEditing} sx={{ fontSize: '0.7rem', minWidth: 0 }}>Done</Button>
          {/* Materialising is otherwise a one-way door: the tokens are gone, and with them
              this project's link to wording edited anywhere else. */}
          <Button
            size="small"
            color="inherit"
            onClick={() => { onReset(); stopEditing(); }}
            sx={{ fontSize: '0.7rem', minWidth: 0, color: 'text.disabled' }}
          >
            Reset to shared defaults
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        {/* One click target for "show me the wording", one icon for "let me change it".
            The caret is what says the row IS a row: without it, a line of grey text beside
            a pencil reads as a caption for the pencil rather than as something to open.
            No hover tooltip — the caret already says the wording is one click away, and a
            paragraph appearing under the pointer on the way past is in the way. */}
        <ButtonBase
          onClick={() => setMode(mode === 'collapsed' ? 'expanded' : 'collapsed')}
          aria-expanded={mode === 'expanded'}
          aria-controls={regionId}
          // Spelled out rather than left to the row's own text, which runs the labels
          // straight into the "defaults" tag ("Timing · Visualdefaults").
          aria-label={usingDefaults ? `${summary} — using shared defaults` : summary}
          sx={{
            flex: 1, minWidth: 0, justifyContent: 'flex-start', gap: 0.25,
            px: 0.5, py: 0.4, borderRadius: 1,
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: 16, flexShrink: 0, color: 'text.disabled',
              transform: mode === 'expanded' ? 'none' : 'rotate(-90deg)',
              transition: 'transform 120ms',
            }}
          />
          <Typography noWrap variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', mr: 0.5 }}>
            {summary}
          </Typography>
          {usingDefaults && (
            <Typography
              variant="caption"
              sx={{ color: 'text.disabled', fontSize: '0.65rem', opacity: 0.7, flexShrink: 0 }}
            >
              defaults
            </Typography>
          )}
        </ButtonBase>
        <Tooltip title={briefDefaults ? 'Use different wording on this project' : 'Loading the shared wording…'}>
          {/* A disabled button fires no events, so the tooltip needs a wrapper to sit on. */}
          <span>
            <IconButton
              ref={pencilRef}
              size="small"
              disabled={!briefDefaults}
              aria-label={`Edit ${label.toLowerCase()} defaults`}
              onClick={startEditing}
              sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
            >
              <EditOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {mode === 'expanded' && (
        <Typography
          id={regionId}
          role="region"
          variant="caption"
          // Full-strength text, not the dim grey of the collapsed row: once it is open you
          // are reading it, and wording you have to squint at is wording nobody checks.
          sx={{
            display: 'block', whiteSpace: 'pre-wrap', color: 'text.primary',
            pl: `${STANDING_TEXT_INSET}px`, pr: 0.75, pb: 0.5,
            fontSize: '0.72rem', lineHeight: 1.5,
          }}
        >
          {briefDefaults ? resolved : 'Loading the shared wording…'}
        </Typography>
      )}
    </Box>
  );
}

// A brief section: the lines the user fills, with the standing wording collapsed
// underneath (see StandingBlock).
//
// A textarea cannot style one line differently from another, so the section is split by a
// rule the templates already follow: a line either carries a [DEFAULT …] token (or one of
// the section's standing labels) or it does not. That rule lives with the templates in
// ../briefTemplates, alongside the recombine that puts an edit back where it came from.
function BriefSection({
  label, template, value, onChange, standingPrefixes, briefDefaults, missing, missingHint,
}: {
  label: string; template: string; value: string; onChange: (v: string) => void;
  standingPrefixes: string[];
  briefDefaults: Record<BriefDefaultKey, string> | null;
  missing?: boolean; missingHint?: string;
}) {
  // The dot reads "has the user said anything here", not "is the string non-empty" — a
  // section holding nothing but its own template is not an answer, and calling it one
  // put a green Set dot on the same row that Generate then marked Required.
  const written = isSectionWritten(value, standingPrefixes);

  // Clicking in shows the template as real, editable text so there is something concrete
  // to work with — but LOCALLY. Storing it on focus meant tabbing through the form
  // autosaved a section nobody had touched, which then had to be told apart from a
  // section nobody had opened, in this repo and in the backend that renders it.
  const [seeded, setSeeded] = React.useState(false);
  const stored = value.trim() ? value : '';

  // The hint always comes from the template. Taking it from the current value meant that
  // clearing the top box to retype it left a blank box with no hint and no way back.
  const hint = splitBriefLines(template, standingPrefixes);
  const storedSplit = splitBriefLines(stored, standingPrefixes);
  // Whether the section has a line to type into AT ALL. A stored one can have none — its
  // fields cleared, or seeded by an older template — and gating the seed on "is anything
  // stored" left that box empty for good: the grey hint showed what belonged there and
  // clicking in did nothing, because the standing wording underneath counted as content.
  const hasInputs = !!storedSplit.inputs.trim();

  // What the boxes are read from and written back into. The standing row reflects the
  // TEMPLATE while nothing is stored — what the brief will say is not a secret until you
  // click into it — and the template's input lines are grafted in FRONT of stored standing
  // wording when they are missing, so a restored line lands above them, not after.
  const base = !stored
    ? template
    : (hasInputs ? stored : [hint.inputs, storedSplit.defaults].filter((part) => part).join('\n'));
  const split = splitBriefLines(base, standingPrefixes);
  const emit = (nextInputs: string, nextDefaults: string) => (
    onChange(recombineBriefLines(split, nextInputs, nextDefaults))
  );

  return (
    <Box
      sx={{ mb: 1.5 }}
      onBlur={(e) => {
        // Left the section without typing anything: drop the locally-seeded template so
        // the box goes back to a grey hint rather than showing text as though it were
        // stored. Ignored while focus moves within the section.
        if (!hasInputs && !e.currentTarget.contains(e.relatedTarget as Node | null)) setSeeded(false);
      }}
    >
      <FieldHeader
        label={label}
        set={written}
        missing={missing}
        optional={isOptionalSection(template, standingPrefixes)}
      />
      {/* The hint and what clicking in fills the box with are the SAME string, straight
          off the template — a hint that rewrites itself the moment you click it reads as
          the form having lost something. */}
      <TextField
        value={hasInputs || seeded ? split.inputs : ''}
        onChange={(e) => {
          // Cleared it themselves: stop showing the seeded template, or the line they just
          // deleted reappears under their cursor.
          if (!e.target.value.trim()) setSeeded(false);
          emit(e.target.value, split.defaults);
        }}
        onFocus={() => setSeeded(true)}
        placeholder={hint.inputs}
        multiline
        minRows={2}
        maxRows={10}
        fullWidth
        size="small"
        helperText={missing ? missingHint : undefined}
        FormHelperTextProps={{ sx: { color: MISSING_TEXT, fontSize: '0.7rem', ml: 0.5 } }}
        InputProps={{ sx: { fontSize: '0.78rem', lineHeight: 1.45 } }}
        sx={missing ? {
          backgroundColor: 'rgba(224,163,163,0.08)', borderRadius: 1,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: MISSING_RED, borderWidth: 2 },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: MISSING_RED },
        } : undefined}
      />
      {hint.defaults && (
        <StandingBlock
          label={label}
          block={split.defaults}
          templateBlock={hint.defaults}
          briefDefaults={briefDefaults}
          onChange={(nextDefaults) => emit(split.inputs, nextDefaults)}
          onReset={() => emit(split.inputs, hint.defaults)}
        />
      )}
    </Box>
  );
}

function BriefChoice({ label, value, onChange }: {
  label: string; value: string; onChange: (v: string) => void;
}) {
  return (
    <Box sx={{ mb: 1.5 }}>
      {/* No dot: a dropdown that always holds one of two values can never be unset, so a
          permanently-green light beside it is decoration rather than information. */}
      <FieldHeader label={label} set dot={false} />
      <TextField
        select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
        size="small"
        SelectProps={{ inputProps: { 'aria-label': label } }}
        InputProps={{ sx: { fontSize: '0.82rem' } }}
      >
        {BRIEF_CHOICES.map((choice) => (
          <MenuItem key={choice.value} value={choice.value}>{choice.label}</MenuItem>
        ))}
      </TextField>
    </Box>
  );
}

// The set/unset dot + caption shared by the product dropdown and every copy field.
//
// Sentence case at normal weight: a column of bold ALL-CAPS headers shouts every label
// equally loudly, which is what made a form with two required answers read as six.
function FieldHeader({ label, set, missing, dot = true, optional }: {
  label: string; set: boolean; missing?: boolean; dot?: boolean; optional?: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
      {dot && (
        <Tooltip title={set ? 'Set' : 'Not set yet'}>
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
            backgroundColor: set ? '#16a34a' : 'transparent',
            border: set ? 'none' : '2px solid #cbd5e1',
          }} />
        </Tooltip>
      )}
      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
        {label}
      </Typography>
      {missing
        ? <Typography variant="caption" sx={{ color: MISSING_TEXT, fontWeight: 700, ml: 'auto' }}>Required</Typography>
        : (!set && (
          <Typography variant="caption" sx={{ color: 'text.disabled', ml: 'auto' }}>
            {/* Every line this section asks for is optional, so "Not set" would read as an
                outstanding job rather than a choice not taken. */}
            {optional ? 'Optional' : 'Not set'}
          </Typography>
        ))}
    </Box>
  );
}

function CopyField({ label, hint, value, onChange }: {
  label: string; hint: string; value: string; onChange: (v: string) => void;
}) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <FieldHeader label={label} set={!!value.trim()} />
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={hint}
        multiline
        minRows={2}
        maxRows={5}
        fullWidth
        size="small"
        InputProps={{ sx: { fontSize: '0.82rem' } }}
      />
    </Box>
  );
}

// `none` means "no product-like item is on screen at all". It exists so a b-roll
// clip of an empty room can be labeled honestly; as the product an ad is selling it
// is meaningless, so it is left out of this dropdown.
const NO_PRODUCT_CATEGORY = 'none';

export default function ProjectCopyPanel({
  productCategory, headline, vocabulary,
  onProductCategoryChange, onHeadlineChange,
  hookOverlay, editingDirection, finalCta, subtitlesStyle, musicStyle, onBriefFieldChange,
  onEditDefaults, missingRequired, briefDefaults,
}: Props) {
  const categories = (vocabulary?.dimensions.find(d => d.key === 'productCategory')?.values || [])
    .filter(v => v.value !== NO_PRODUCT_CATEGORY);
  // Until the vocabulary loads there are no options, so a saved value would render
  // as a blank select and a stray change would clear it. Show "Not set" instead.
  const selected = categories.some(c => c.value === productCategory) ? productCategory : '';
  return (
    <Box sx={{
      flexShrink: 0, width: 380,
      // The stage is a fixed height and this panel is taller than it, so it owns a
      // scroller: minHeight:0 lets the flex parent shrink it to the stage rather than
      // to its content, and the overflow keeps it inside its own box instead of
      // spilling over the timeline below.
      minHeight: 0, overflowY: 'auto',
      p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: '#f8fafc',
    }}>
      <Box sx={{ mb: 2 }}>
        <FieldHeader label="Product type" set={!!productCategory} />
        <TextField
          select
          value={selected}
          onChange={(e) => onProductCategoryChange(e.target.value)}
          disabled={!vocabulary}
          fullWidth
          size="small"
          SelectProps={{ displayEmpty: true, inputProps: { 'aria-label': 'Product type' } }}
          InputProps={{ sx: { fontSize: '0.82rem' } }}
        >
          <MenuItem value=""><em>{vocabulary ? 'Not set' : 'Loading…'}</em></MenuItem>
          {categories.map(({ value }) => (
            <MenuItem key={value} value={value}>{humanize(value)}</MenuItem>
          ))}
        </TextField>
      </Box>
      {/* In the order the generated instructions read. There is no separate Hook or CTA
          field: the hook IS the first line of the opening overlay and the CTA IS the
          copy line of the final screen, so a second box for each only created two
          places to say the same thing and a question about which one wins. */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">Brief settings</Typography>
        <Tooltip title="Edit the wording every brief starts from">
          <IconButton size="small" onClick={onEditDefaults} sx={{ ml: 'auto' }} aria-label="Edit brief defaults">
            <SettingsOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <BriefSection
        label="Opening hook overlay"
        template={HOOK_OVERLAY_TEMPLATE}
        standingPrefixes={HOOK_OVERLAY_STANDING_PREFIXES}
        briefDefaults={briefDefaults}
        value={hookOverlay}
        onChange={(v) => onBriefFieldChange('hookOverlay', v)}
        missing={!!missingRequired?.hook}
        missingHint="Write the hook — the words on screen at the start."
      />
      <BriefSection
        label="Editing direction"
        template={EDITING_DIRECTION_TEMPLATE}
        standingPrefixes={EDITING_DIRECTION_STANDING_PREFIXES}
        briefDefaults={briefDefaults}
        value={editingDirection}
        onChange={(v) => onBriefFieldChange('editingDirection', v)}
      />
      <BriefSection
        label="Final CTA screen"
        template={FINAL_CTA_TEMPLATE}
        standingPrefixes={FINAL_CTA_STANDING_PREFIXES}
        briefDefaults={briefDefaults}
        value={finalCta}
        onChange={(v) => onBriefFieldChange('finalCta', v)}
        missing={!!missingRequired?.cta}
        missingHint="Write the closing copy — the words on the last screen."
      />
      <BriefChoice label="Subtitles" value={subtitlesStyle} onChange={(v) => onBriefFieldChange('subtitlesStyle', v)} />
      <BriefChoice label="Music" value={musicStyle} onChange={(v) => onBriefFieldChange('musicStyle', v)} />

      {/* The headline never appears on screen — it lives on the Meta ad — so it is
          kept apart from the brief the video editor works from. */}
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Ad copy</Typography>
      <CopyField label="Headline" hint="Meta ad headline — generate ideas from “Hook ideas”." value={headline} onChange={onHeadlineChange} />
    </Box>
  );
}
